import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loading, EmptyState } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, Users, Phone, MapPin, Download, ChevronRight } from "lucide-react";

export default function Clienti() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", cognome: "", phone: "", comune: "", email: "", note: "" });
  const nav = useNavigate();
  const { t } = useTranslation();

  const load = () => http.get("/customers", { params: { q } }).then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { const tm = setTimeout(load, 250); return () => clearTimeout(tm); }, [q]);

  const create = async () => {
    try { const r = await http.post("/customers", form); toast.success(t("clienti.created")); setOpen(false); setForm({ name: "", cognome: "", phone: "", comune: "", email: "", note: "" }); nav(`/clienti/${r.data.id}`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("clienti.title")}</h1>
        <div className="flex gap-2">
          <a href={`${http.defaults.baseURL}/export/customers`} download>
            <Button variant="outline" className="gap-1.5" data-testid="export-customers-btn"><Download className="w-4 h-4" /><span className="hidden sm:inline">CSV</span></Button>
          </a>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-customer-btn" className="bg-[#0F172A] hover:bg-[#1E293B] gap-1.5"><Plus className="w-4 h-4" /> {t("clienti.new")}</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>{t("clienti.add")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                {[["name", `${t("clienti.full_name")}*`], ["cognome", t("clienti.surname")], ["phone", `${t("clienti.phone_wa")}*`], ["comune", t("clienti.city")], ["email", t("common.email")]].map(([k, l]) => (
                  <div key={k}><Label>{l}</Label><Input data-testid={`customer-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" /></div>
                ))}
                <div className="col-span-2"><Label>{t("clienti.note")}</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" /></div>
              </div>
              <DialogFooter><Button data-testid="save-customer-btn" onClick={create} disabled={!form.name || !form.phone}>{t("common.save")}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="customers-search" placeholder={t("clienti.search_full")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" /></div>

      {!list ? <Loading /> : list.length === 0 ? <EmptyState icon={Users} title={t("clienti.empty")} hint={t("clienti.empty_hint2")} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((c) => (
            <Card key={c.id} data-testid="customer-card" onClick={() => nav(`/clienti/${c.id}`)} className="p-4 cursor-pointer hover:shadow-md transition-shadow border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0F172A] text-white flex items-center justify-center font-bold shrink-0">{c.name?.[0]?.toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#0F172A] truncate flex items-center gap-1.5">{c.name} {c.cognome} {c.is_demo && <span className="text-[9px] bg-violet-100 text-violet-700 px-1.5 rounded">DEMO</span>}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300" />
              </div>
              {c.comune && <div className="text-xs text-slate-500 mt-2 flex items-center gap-1"><MapPin className="w-3 h-3" />{c.comune}</div>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
